import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

dayjs.extend(utc);
dayjs.extend(timezone);

const FALLBACK_TIMEZONE = "America/New_York";

export const getUserTimezone = () => {
  try {
    const guessed = dayjs.tz.guess();
    if (guessed) {
      return guessed;
    }
  } catch (_) {
    // fall through
  }

  return FALLBACK_TIMEZONE;
};

export const nowInUserTimezone = () => dayjs().tz(getUserTimezone());

export const todayInUserTimezone = () =>
  nowInUserTimezone().format("YYYY-MM-DD");

export const getHourFromTimeOption = (timeOption) => {
  if (!timeOption?.hours || !timeOption?.amPm) return null;

  if (timeOption.hours === "12") {
    return timeOption.amPm === "AM" ? 0 : 12;
  }

  const hours = parseInt(timeOption.hours, 10);
  return timeOption.amPm === "PM" ? hours + 12 : hours;
};

/** Calendar date in user TZ for API (YYYY-MM-DD). */
export const formatDateForApi = (dateValue) => {
  if (!dateValue) {
    return todayInUserTimezone();
  }

  if (typeof dateValue === "string" && /^\d{4}-\d{2}-\d{2}/.test(dateValue)) {
    return dateValue.slice(0, 10);
  }

  return dayjs(dateValue).tz(getUserTimezone()).format("YYYY-MM-DD");
};

/** Picker {hours, minutes, amPm} → offset ISO for API. */
export const formatWallClockForApi = (timeOption, dateValue) => {
  const tz = getUserTimezone();
  const hour = getHourFromTimeOption(timeOption);
  const minutes = parseInt(timeOption?.minutes, 10);

  if (hour === null || Number.isNaN(minutes)) {
    return null;
  }

  const datePart = formatDateForApi(dateValue);
  const wallClock = `${datePart} ${String(hour).padStart(2, "0")}:${String(
    minutes
  ).padStart(2, "0")}:00`;

  return dayjs
    .tz(wallClock, "YYYY-MM-DD HH:mm:ss", tz)
    .format("YYYY-MM-DDTHH:mm:ssZ");
};

/** dayjs/Date instant → offset ISO for API. */
export const formatInstantForApi = (selectedTime) => {
  const tz = getUserTimezone();
  const value = selectedTime ? dayjs(selectedTime) : dayjs();
  return value.tz(tz).format("YYYY-MM-DDTHH:mm:ssZ");
};

const parseApiInstant = (timeValue) => {
  if (!timeValue) return null;

  if (
    typeof timeValue === "string" &&
    /^\d{2}:\d{2}(?::\d{2})?$/.test(timeValue.trim())
  ) {
    const [hour, minute, second = "0"] = timeValue.trim().split(":");
    return dayjs()
      .tz(getUserTimezone())
      .hour(Number(hour))
      .minute(Number(minute))
      .second(Number(second));
  }

  const parsed = dayjs(timeValue);
  return parsed.isValid() ? parsed.tz(getUserTimezone()) : null;
};

/** API offset ISO (or legacy value) → display "hh:mm A" in the user timezone. */
export const formatApiTimeForDisplay = (timeValue) => {
  const parsed = parseApiInstant(timeValue);
  return parsed ? parsed.format("hh:mm A") : "";
};

/** API value → picker {hours, minutes, amPm} in the user timezone. */
export const getTimeOptionFromApi = (timeValue) => {
  const parsed = parseApiInstant(timeValue);

  if (!parsed) {
    return { hours: "", minutes: "", amPm: "AM" };
  }

  return {
    hours: parsed.format("hh"),
    minutes: parsed.format("mm"),
    amPm: parsed.format("A"),
  };
};

export const buildLogDateTime = (dateValue, timeOption) => {
  const tz = getUserTimezone();
  const hour = getHourFromTimeOption(timeOption);

  if (
    !dateValue ||
    hour === null ||
    timeOption?.minutes === undefined ||
    timeOption?.minutes === ""
  ) {
    return null;
  }

  const datePart = formatDateForApi(dateValue);
  const wallClock = `${datePart} ${String(hour).padStart(2, "0")}:${String(
    parseInt(timeOption.minutes, 10)
  ).padStart(2, "0")}:00`;

  return dayjs.tz(wallClock, "YYYY-MM-DD HH:mm:ss", tz);
};

export const isFutureLogDateTime = (dateValue, timeOption) => {
  const datetime = buildLogDateTime(dateValue, timeOption);
  return datetime ? datetime.isAfter(nowInUserTimezone()) : false;
};
